import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { tryT, type MessageKey } from '@reos/i18n';
import { FieldRow, FormSection } from '@redhand/ui';
import { PageHead } from '../components/page-head.js';
import { FieldGrid, FormLayout } from '../components/form-layout.js';
import { useT } from '../lib/i18n.js';
import { errorMessage, formatPercent } from '../lib/properties.js';
import { getBillingOverview } from '../lib/billing-api.js';
import {
  createOfficePlanCheckout,
  getOfficePlanOffers,
  type OfficePlanBilling,
  type OfficePlanOffer,
} from '../lib/office-plan-api.js';
import { baseCodeOf, isYearlyCode, yearlyMonthsCharged } from '../lib/plan-periods.js';
import { useSession } from '../stores/session.js';

/**
 * `/tarif` — krok QAFIX9 (zaverecne QA 22. 10. 2026, nalez QA-ZAV-N1).
 *
 * CO BYLO ROZBITE: „Nastaveni -> Kancelar -> Vas tarif -> Objednat tarif
 * v ceniku" vedlo na VEREJNY `/cenik` a odtud na `/aktivace`, coz je
 * vyhradne REGISTRACE NOVE KANCELARE (`activateOffice()`). Prihlasena
 * kancelar si tedy tarif pres UI koupit nemohla, aniz by vznikla duplicitni
 * kancelar. Rozhodnuti R.B. 22. 10.: objednavka UVNITR aplikace, zadne ID
 * kancelare do URL jine domeny.
 *
 * ZADNA CENA NEZIJE NA TETO OBRAZOVCE. `GET /v1/office-plan/offers` posila
 * hotove castky z CENIKU (`commerce_products` -> `officePlanOffersOf`):
 * zaklad DPH, sazbu, cenu s DPH, limit makleru i cenu dalsiho maklera nad
 * limit. „Rocne = 10 mesicu z 12" je rozdil DVOU katalogovych cen
 * (`yearlyMonthsCharged`), ne cislo v textu.
 *
 * Tarif se NEOBJEDNAVA na nemovitost (kupuje se na ucet) a kancelar se do
 * tela pozadavku NEPOSILA - tenant bere server z prihlasene session.
 *
 * NENI V LEVEM MENU zamerne: `lib/nav.ts` a `nav.spec` jsou chranene meridlo
 * na pocet polozek a tenhle tok ma jedine spravne vchody — karta „Vas tarif"
 * a pruh konce zkusebniho obdobi (oba pres `trial.payUrl`, viz
 * `office-trial.ts`).
 *
 * KROK QAFIX9b (vzhled a texty, snimek `/tmp/qafix9-tarif.png`): obrazovka
 * byla syre HTML — hole `input[type=radio]`, prepinac i „Zaplatit" jako
 * obycejny text, formular bez popisku. Sazba je proto cela ze STAVAJICICH
 * komponent adminu, zadny novy vzor:
 *  - rozvrzeni a prilepena lista akce  ← `FormLayout` (Novy klient/faktura),
 *  - karta sekce a popisky poli        ← `FormSection`/`FieldRow` z kitu,
 *  - prepinac obdobi                   ← `.rh-a2-toggle` (tyz jako osoba/firma),
 *  - volitelna karta tarifu            ← `.rh-v4-typecard` (tyz jako typ smlouvy).
 */

/** Obdobi, ktere prepinac vybira. Je to VYBER RADKU CENIKU, ne prepocet. */
type Period = 'MONTHLY' | 'YEARLY';

function money(amount: string, currency: string): string {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount));
}

/** Nazev tarifu z i18n katalogu; bez klice se ukaze KOD, nikdy klic. */
function planName(offer: OfficePlanOffer): string {
  return tryT(offer.nameKey as MessageKey, { locale: 'cs' }) ?? offer.productCode;
}

const EMPTY_BILLING: OfficePlanBilling = { name: '' };

/** Prazdny formular = nic do nej jeste nenapsal ani uzivatel, ani predvyplneni. */
function isBillingEmpty(billing: OfficePlanBilling): boolean {
  return Object.values(billing).every((value) => value === undefined || value === '');
}

export function TarifPage(): React.JSX.Element {
  const t = useT();
  const offers = useQuery({ queryKey: ['tarif', 'offers'], queryFn: getOfficePlanOffers });
  /*
   * QAFIX9b bod 3 — PREDVYPLNENI Z PROFILU KANCELARE. Fakturacni udaje uz
   * kancelar jednou zadala do „Fakturujici subjekt" (krok FAK1) a opisovat je
   * znovu je presne ta prace, kterou si makler nema delat. Dodavatel je i
   * SPRAVNY zdroj: jmeno, ICO, DIC a adresa, ktere jdou na doklad.
   *
   * Chyba (neni dodavatel, chybi pravo) obrazovku NESMI shodit — objednat
   * tarif se musi dat i s rucne vyplnenym formularem, proto `null`.
   */
  const profile = useQuery({
    queryKey: ['tarif', 'billing-profile'],
    queryFn: async () => {
      try {
        return await getBillingOverview();
      } catch {
        return null;
      }
    },
  });
  /*
   * Krok PRO1 — ZAVRENY CHECKOUT (`commerce.legal_approved === false`).
   *
   * Server by objednavku odmitl 409 `commerce/legal-not-approved`
   * (`createOfficePlanCheckout`), takze aktivni tlacitko „Zaplatit" by
   * koncilo chybovou hlaskou. Stav chodi ze session (`trial.checkoutOpen`),
   * tedy z TEHOZ zdroje jako karta v Nastaveni — obrazovka si ho nepocita.
   * Chybejici pole (starsi API) nic nezavira.
   */
  const checkoutClosed = useSession((s) => s.trial?.checkoutOpen) === false;
  const [period, setPeriod] = React.useState<Period>('MONTHLY');
  const [selected, setSelected] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState('');
  const [billing, setBilling] = React.useState<OfficePlanBilling>(EMPTY_BILLING);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const items = offers.data?.items ?? [];
  /** Radky obdobi, ktere prepinac prave ukazuje (`<kod>_YEARLY` = rocni). */
  const shown = items.filter((offer) =>
    period === 'YEARLY' ? isYearlyCode(offer.productCode) : !isYearlyCode(offer.productCode),
  );
  const current = shown.find((offer) => offer.productCode === selected) ?? null;

  /*
   * Predvyplneni bezi JEDNOU a jen do PRAZDNEHO formulare: kdyz odpoved
   * dodavatele dojde pozdeji nez prvni znak uzivatele, nesmi mu to prepsat,
   * co uz napsal.
   */
  const prefilled = React.useRef(false);
  const supplier = profile.data?.supplier ?? null;
  React.useEffect(() => {
    if (prefilled.current || supplier === null) return;
    prefilled.current = true;
    const next: OfficePlanBilling = {
      name: supplier.companyName,
      ...(supplier.ico === '' ? {} : { ico: supplier.ico }),
      ...(supplier.dic === null ? {} : { vatId: supplier.dic }),
      ...(supplier.street === null ? {} : { street: supplier.street }),
      ...(supplier.city === null ? {} : { city: supplier.city }),
      ...(supplier.zip === null ? {} : { zip: supplier.zip }),
    };
    setBilling((cur) => (isBillingEmpty(cur) ? next : cur));
    const supplierEmail = supplier.email;
    if (supplierEmail !== null) setEmail((cur) => (cur === '' ? supplierEmail : cur));
  }, [supplier]);

  /*
   * Po prepnuti obdobi musi zustat TENTYZ tarif, jen v jine variante - jinak
   * by se vyber po kliknuti na „Rocne" tise zahodil.
   */
  function switchPeriod(next: Period): void {
    setPeriod(next);
    setSelected((code) => {
      if (code === null) return null;
      const base = baseCodeOf(code);
      const wanted = items.find(
        (offer) =>
          baseCodeOf(offer.productCode) === base &&
          (next === 'YEARLY') === isYearlyCode(offer.productCode),
      );
      return wanted?.productCode ?? null;
    });
  }

  /** Mesicni radek tehoz tarifu — potrebuje ho poznamka „10 mesicu z 12". */
  function monthsOf(offer: OfficePlanOffer): number | null {
    if (!isYearlyCode(offer.productCode)) return null;
    const monthly = items.find((row) => row.productCode === baseCodeOf(offer.productCode));
    return yearlyMonthsCharged(monthly?.priceNet, offer.priceNet);
  }

  function field(key: keyof OfficePlanBilling): [string, (v: string) => void] {
    return [billing[key] ?? '', (v: string) => setBilling((b) => ({ ...b, [key]: v }))];
  }
  const [nameVal, setName] = field('name');
  const [icoVal, setIco] = field('ico');
  const [vatIdVal, setVatId] = field('vatId');
  const [streetVal, setStreet] = field('street');
  const [cityVal, setCity] = field('city');
  const [zipVal, setZip] = field('zip');

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (current === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createOfficePlanCheckout({
        productCode: current.productCode,
        email,
        billing: { ...billing, country: billing.country ?? 'CZ' },
      });
      // Platebni stranka brany. Navrat vede zpatky SEM (`officePlanReturnUrl`).
      window.location.assign(result.redirectUrl);
    } catch (e2) {
      setError(t('webAdmin.officePlan.error', { error: errorMessage(e2) }));
      setSubmitting(false);
    }
  }

  function periodButton(value: Period, label: string, testId: string): React.JSX.Element {
    return (
      <button
        type="button"
        className={`rh-a2-toggle__btn${period === value ? ' is-on' : ''}`}
        aria-pressed={period === value}
        onClick={() => switchPeriod(value)}
        data-testid={testId}
      >
        {label}
      </button>
    );
  }

  return (
    <>
      <PageHead title={t('webAdmin.officePlan.title')} subtitle={t('webAdmin.officePlan.perex')} />
      <div data-testid="tarif-page">
        {offers.isPending ? (
          <p className="rh-a2-muted">{t('webAdmin.officePlan.loading')}</p>
        ) : offers.isError ? (
          <p className="rh-a2-error" data-testid="tarif-load-error">
            {t('webAdmin.officePlan.error', { error: errorMessage(offers.error) })}
          </p>
        ) : items.length === 0 ? (
          <p className="rh-a2-muted" data-testid="tarif-empty">
            {t('webAdmin.officePlan.empty')}
          </p>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)}>
            <FormLayout
              width="limited"
              aside={
                /*
                 * Souhrn a jedina primarni akce stoji v uzkem sloupci vedle
                 * formulare (`.rh-a2-form-aside` je sticky) — na 390 px se
                 * sloupce slozi pod sebe (`admin-v2.css` @900px).
                 */
                <section className="rh-a2-card" data-testid="tarif-summary-card">
                  <div className="rh-a2-card__head">
                    <h2>{t('webAdmin.officePlan.summaryTitle')}</h2>
                  </div>
                  {current === null ? (
                    <p className="rh-a2-muted" data-testid="tarif-summary-none">
                      {t('webAdmin.officePlan.summaryNone')}
                    </p>
                  ) : (
                    <p data-testid="tarif-summary">
                      {/*
                       * Sazba jde PRES `formatPercent()` stejne jako na kartach
                       * tarifu a ve fakturach — `numeric` „21.00" se cte „21"
                       * a znak `%` nese text katalogu (v EN je bez mezery).
                       */}
                      {t('webAdmin.officePlan.summary', {
                        plan: planName(current),
                        net: money(current.priceNet, current.currency),
                        rate: formatPercent(current.vatRate),
                        gross: money(current.priceGross, current.currency),
                      })}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="rh-a2-btn rh-a2-btn--primary rh-a2-btn--block mt-3"
                    disabled={submitting || current === null || checkoutClosed}
                    data-testid="tarif-pay"
                  >
                    {submitting ? t('webAdmin.officePlan.paying') : t('webAdmin.officePlan.pay')}
                  </button>
                  {/* DUVOD POD TLACITKEM: nedostupne tlacitko bez vysvetleni je
                      slepa ulicka — makler nevi, co mu chybi. */}
                  {checkoutClosed ? (
                    <p className="rh-a2-muted mt-2" data-testid="tarif-checkout-closed">
                      {/* TYZ text jako karta „Kancelář" v Nastaveni — jedna
                          hlaska o zavrene platbe, ne dve. */}
                      {t('webAdmin.settings.officeTrial.checkoutClosed')}
                    </p>
                  ) : current === null ? (
                    <p className="rh-a2-muted mt-2" data-testid="tarif-pay-disabled-reason">
                      {t('webAdmin.officePlan.payDisabledReason')}
                    </p>
                  ) : null}
                  <p className="rh-a2-muted mt-2">{t('webAdmin.officePlan.payNote')}</p>
                  {/* Krok COMGATE1: zpusoby platby OBECNE (karta, prevod,
                      Apple/Google Pay) — zadna znacka brany ani obchodnika. */}
                  <p className="rh-a2-muted mt-2" data-testid="tarif-pay-methods">
                    {t('webAdmin.officePlan.payMethods')}
                  </p>
                  {error === null ? null : (
                    <p role="alert" className="rh-a2-error mt-2" data-testid="tarif-error">
                      {error}
                    </p>
                  )}
                </section>
              }
              main={
                <>
                  <FormSection title={t('webAdmin.officePlan.plansTitle')}>
                    <div
                      className="rh-a2-toggle"
                      role="group"
                      aria-label={t('webAdmin.officePlan.periodLabel')}
                      data-testid="tarif-period"
                    >
                      {periodButton(
                        'MONTHLY',
                        t('webAdmin.officePlan.periodMonthly'),
                        'tarif-period-monthly',
                      )}
                      {periodButton(
                        'YEARLY',
                        t('webAdmin.officePlan.periodYearly'),
                        'tarif-period-yearly',
                      )}
                    </div>

                    {/*
                      KLIK NA CELOU KARTU: karta je `<label>` nad skutecnym
                      `input[type=radio]`, ne tlacitko s `role="radio"` — radio
                      si nese vyber, klavesnicove ovladani i `name` grupy samo
                      a `<label>` z nej dela cely klikaci obdelnik.
                    */}
                    <ul
                      className="rh-a2-stack list-none p-0"
                      style={{ gap: 'var(--rh-space-3)' }}
                      data-testid="tarif-plans"
                    >
                      {shown.map((offer) => {
                        const months = monthsOf(offer);
                        const on = selected === offer.productCode;
                        return (
                          <li key={offer.productCode}>
                            <label className={`rh-v4-typecard${on ? ' is-on' : ''}`}>
                              <span className="flex items-baseline gap-2">
                                <input
                                  type="radio"
                                  name="tarif-plan"
                                  checked={on}
                                  onChange={() => setSelected(offer.productCode)}
                                  data-testid={`tarif-plan-${offer.productCode}`}
                                />
                                <span className="rh-v4-typecard__name">{planName(offer)}</span>
                                <span
                                  className="rh-v4-typecard__name ml-auto"
                                  data-testid={`tarif-price-${offer.productCode}`}
                                >
                                  {money(offer.priceGross, offer.currency)}
                                </span>
                              </span>
                              {offer.current ? (
                                <span
                                  /* `self-start`: karta je sloupcovy flex, bez toho
                                     by se pilulka roztahla na celou sirku. */
                                  className="rh-a2-badge rh-a2-badge--ok self-start"
                                  data-testid={`tarif-current-${offer.productCode}`}
                                >
                                  {t('webAdmin.officePlan.currentBadge')}
                                </span>
                              ) : null}
                              <span
                                className="rh-v4-typecard__note"
                                data-testid={`tarif-breakdown-${offer.productCode}`}
                              >
                                {t('webAdmin.officePlan.priceBreakdown', {
                                  net: money(offer.priceNet, offer.currency),
                                  rate: formatPercent(offer.vatRate),
                                  gross: money(offer.priceGross, offer.currency),
                                })}
                              </span>
                              {offer.includedUsers === null ? null : (
                                <span
                                  className="rh-v4-typecard__note"
                                  data-testid={`tarif-seats-${offer.productCode}`}
                                >
                                  {t('webAdmin.officePlan.seats', {
                                    included: offer.includedUsers,
                                    active: offers.data?.activeUsers ?? 0,
                                  })}
                                </span>
                              )}
                              {offer.extraUsers > 0 && offer.extraUserPriceNet !== null ? (
                                <span
                                  className="rh-v4-typecard__note"
                                  data-testid={`tarif-extra-${offer.productCode}`}
                                >
                                  {t('webAdmin.officePlan.extraUsers', {
                                    count: offer.extraUsers,
                                    perUser: money(offer.extraUserPriceNet, offer.currency),
                                    total: money(offer.extraUsersMonthlyNet, offer.currency),
                                  })}
                                </span>
                              ) : null}
                              {months === null ? null : (
                                <span
                                  className="rh-v4-typecard__note"
                                  data-testid={`tarif-yearly-note-${offer.productCode}`}
                                >
                                  {t('webAdmin.officePlan.yearlyNote', { months })}
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </FormSection>

                  <FormSection
                    title={t('webAdmin.officePlan.billingTitle')}
                    {...(supplier === null
                      ? {}
                      : { description: t('webAdmin.officePlan.billingPrefilled') })}
                  >
                    <FieldRow
                      label={t('webAdmin.officePlan.emailLabel')}
                      htmlFor="tarif-email"
                      required
                    >
                      <input
                        id="tarif-email"
                        className="rh-input block"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="tarif-email"
                      />
                    </FieldRow>
                    <FieldRow
                      label={t('webAdmin.officePlan.billingNameLabel')}
                      htmlFor="tarif-billing-name"
                      required
                    >
                      <input
                        id="tarif-billing-name"
                        className="rh-input block"
                        required
                        value={nameVal}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="tarif-billing-name"
                      />
                    </FieldRow>
                    <FieldGrid columns="1fr 1fr">
                      <FieldRow
                        label={t('webAdmin.officePlan.billingIcoLabel')}
                        htmlFor="tarif-billing-ico"
                      >
                        <input
                          id="tarif-billing-ico"
                          className="rh-input block"
                          value={icoVal}
                          onChange={(e) => setIco(e.target.value)}
                          data-testid="tarif-billing-ico"
                        />
                      </FieldRow>
                      <FieldRow
                        label={t('webAdmin.officePlan.billingVatIdLabel')}
                        htmlFor="tarif-billing-vatid"
                      >
                        <input
                          id="tarif-billing-vatid"
                          className="rh-input block"
                          value={vatIdVal}
                          onChange={(e) => setVatId(e.target.value)}
                          data-testid="tarif-billing-vatid"
                        />
                      </FieldRow>
                    </FieldGrid>
                    <FieldRow
                      label={t('webAdmin.officePlan.billingStreetLabel')}
                      htmlFor="tarif-billing-street"
                    >
                      <input
                        id="tarif-billing-street"
                        className="rh-input block"
                        value={streetVal}
                        onChange={(e) => setStreet(e.target.value)}
                        data-testid="tarif-billing-street"
                      />
                    </FieldRow>
                    <FieldGrid columns="2fr 1fr">
                      <FieldRow
                        label={t('webAdmin.officePlan.billingCityLabel')}
                        htmlFor="tarif-billing-city"
                      >
                        <input
                          id="tarif-billing-city"
                          className="rh-input block"
                          value={cityVal}
                          onChange={(e) => setCity(e.target.value)}
                          data-testid="tarif-billing-city"
                        />
                      </FieldRow>
                      <FieldRow
                        label={t('webAdmin.officePlan.billingZipLabel')}
                        htmlFor="tarif-billing-zip"
                      >
                        <input
                          id="tarif-billing-zip"
                          className="rh-input block"
                          value={zipVal}
                          onChange={(e) => setZip(e.target.value)}
                          data-testid="tarif-billing-zip"
                        />
                      </FieldRow>
                    </FieldGrid>
                  </FormSection>
                </>
              }
            />
          </form>
        )}
      </div>
    </>
  );
}
